import { useCallback, useMemo, useState } from "react";

import { listGradeAgrupadores, listGradeSizes } from "../utils/family-builder";
import { buildFamilyGradeScoped } from "../../shared/utils/family-grade";

/** Filtros da sidebar do catálogo (agrupador, nome comercial, vitrine, tamanho) + busca por texto. */
export default function useProductFilters({ effectiveFamilies, currentBrand }) {
  const [selectedAgrupadores, setSelectedAgrupadores] = useState(new Set());
  const [selectedCommercialNames, setSelectedCommercialNames] = useState(
    new Set(),
  );
  const [selectedVitrines, setSelectedVitrines] = useState(new Set());
  const [selectedSizes, setSelectedSizes] = useState(new Set());
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // useCallback + updater funcional: mantém a identidade da função estável entre
  // renders para que a Sidebar (memoizada) não re-renderize a cada tecla digitada
  // num campo de quantidade (que altera o orderState no nível da página).
  const toggleFilter = useCallback((type, value) => {
    const setterByType = {
      agrupador: setSelectedAgrupadores,
      commercial: setSelectedCommercialNames,
      vitrine: setSelectedVitrines,
      size: setSelectedSizes,
    };
    const setSet = setterByType[type];
    if (!setSet) return;
    setSet((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  }, []);

  const filteredFamilies = useMemo(() => {
    const brandFamilies = effectiveFamilies[currentBrand];
    return Object.keys(brandFamilies || {}).filter((key) => {
      const fam = brandFamilies[key];

      const agrupadorMatch =
        selectedAgrupadores.size === 0 ||
        listGradeAgrupadores(fam).some((s) => selectedAgrupadores.has(s));

      const commLabel = String(
        fam.gradeVenda || fam.commercialName || fam.productDisplayName || "",
      ).trim();
      const commMatch =
        selectedCommercialNames.size === 0 ||
        selectedCommercialNames.has(commLabel);

      const vitrineMatch =
        selectedVitrines.size === 0 ||
        selectedVitrines.has(fam.vitrine) ||
        (fam.products || []).some((p) => selectedVitrines.has(p.vitrine));

      const sizesInFam = new Set(listGradeSizes(fam));
      const sizeMatch =
        selectedSizes.size === 0 ||
        [...sizesInFam].some((s) => selectedSizes.has(s));

      return agrupadorMatch && commMatch && vitrineMatch && sizeMatch;
    });
  }, [
    currentBrand,
    effectiveFamilies,
    selectedAgrupadores,
    selectedCommercialNames,
    selectedVitrines,
    selectedSizes,
  ]);

  // Uma mesma grade comercial pode existir em mais de uma vitrine que a loja enxerga —
  // nesse caso os produtos das duas vitrines caem no mesmo card (family-builder.js agrupa
  // por grade, não por vitrine). O filtro acima decide corretamente SE o card aparece
  // (basta um produto bater), mas sem isto o card renderizaria TODOS os produtos dele,
  // inclusive os de vitrines não selecionadas. Aqui recorta `products`/`grade` do card pra
  // só os produtos da(s) vitrine(s) selecionada(s), sem mutar o objeto original (que fica
  // em cache/effectiveFamilies e é reusado quando o filtro é limpo).
  const scopedFamilies = useMemo(() => {
    if (selectedVitrines.size === 0) return effectiveFamilies;

    const brandFamilies = effectiveFamilies[currentBrand];
    if (!brandFamilies) return effectiveFamilies;

    const scopedBrandFamilies = {};
    Object.keys(brandFamilies).forEach((key) => {
      const fam = brandFamilies[key];
      const allProducts = fam.products || [];
      const scoped = allProducts.filter(
        (p) => p.vitrine != null && selectedVitrines.has(p.vitrine),
      );

      if (scoped.length === 0 || scoped.length === allProducts.length) {
        // Nada pra recortar (família já é de uma vitrine só, ou nenhum produto bate —
        // este segundo caso não deveria aparecer em filteredFamilies mesmo).
        scopedBrandFamilies[key] = fam;
        return;
      }

      scopedBrandFamilies[key] = {
        ...fam,
        products: scoped,
        grade:
          fam.renderType === "rows" ? fam.grade : buildFamilyGradeScoped(scoped),
      };
    });

    return { ...effectiveFamilies, [currentBrand]: scopedBrandFamilies };
  }, [effectiveFamilies, currentBrand, selectedVitrines]);

  return {
    selectedAgrupadores,
    selectedCommercialNames,
    selectedVitrines,
    selectedSizes,
    productSearchTerm,
    setProductSearchTerm,
    toggleFilter,
    filteredFamilies,
    scopedFamilies,
  };
}
